import * as Sentry from "@sentry/react";
import ReactGA from "react-ga4";
import { getAppVersion } from "./version";

// Sentry Configuration for Error Tracking
export const initSentry = () => {
  const APP_VERSION = getAppVersion();
  const DSN = process.env.REACT_APP_SENTRY_DSN || "";

  // Only initialize in production AND when a DSN is configured
  if (process.env.NODE_ENV === 'production' && DSN) {
    Sentry.init({
      dsn: DSN,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: 0.1, // Capture 10% of transactions for performance monitoring
      // Session Replay
      replaysSessionSampleRate: 0.1, // Capture 10% of sessions
      replaysOnErrorSampleRate: 1.0, // Capture 100% of sessions with errors

      // Environment
      environment: process.env.NODE_ENV,

      // Release tracking — used by Sentry to group regressions per build
      release: APP_VERSION,

      // Attach app_version + side ('frontend') as tags on EVERY event so we can
      // filter / search in the Sentry dashboard without parsing release strings.
      initialScope: {
        tags: {
          app_version: APP_VERSION,
          side: "frontend",
        },
      },
      
      // Ignore certain errors
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        // Random plugins/extensions
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        'Can\'t find variable: ZiteReader',
        'jigsaw is not defined',
        'ComboSearch is not defined',
        // Facebook
        'fb_xd_fragment',
        // Network errors
        'NetworkError',
        'Non-Error promise rejection captured'
      ],
    });

    // Expose Sentry on window so we can manually trigger test events from
    // DevTools, e.g. `window.Sentry.captureException(new Error("test"))`.
    if (typeof window !== "undefined") {
      window.Sentry = Sentry;
    }
  }
};

// Google Analytics 4 Configuration
export const initGA4 = () => {
  const GA_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "G-XXXXXXXXXX"; // Add your GA4 Measurement ID
  
  if (GA_MEASUREMENT_ID !== "G-XXXXXXXXXX") {
    ReactGA.initialize(GA_MEASUREMENT_ID, {
      gaOptions: {
        siteSpeedSampleRate: 100,
      },
    });
  }
};

// Track Page Views
export const trackPageView = (path, title) => {
  ReactGA.send({ 
    hitType: "pageview", 
    page: path,
    title: title || document.title 
  });
  
  // Also track in Sentry for context
  if (process.env.NODE_ENV === 'production') {
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: `Navigated to ${path}`,
      level: 'info',
    });
  }
};

// Track Custom Events
export const trackEvent = (category, action, label, value) => {
  ReactGA.event({
    category: category,
    action: action,
    label: label,
    value: value,
  });
  
  // Also log to Sentry for debugging
  if (process.env.NODE_ENV === 'production') {
    Sentry.addBreadcrumb({
      category: 'user-action',
      message: `${category}: ${action}`,
      data: { label, value },
      level: 'info',
    });
  }
};

// Track User Login
export const trackLogin = (method = 'email') => {
  trackEvent('Authentication', 'Login', method);
  
  ReactGA.event('login', {
    method: method,
  });
};

// Track User Registration
export const trackRegistration = (method = 'email') => {
  trackEvent('Authentication', 'Registration', method);
  
  ReactGA.event('sign_up', {
    method: method,
  });
};

// Track Feature Usage
export const trackFeatureUsage = (featureName, action = 'use') => {
  trackEvent('Feature Usage', action, featureName);
};

// Track Build Tracker Actions
export const trackBuildTrackerEvent = (action, metadata = {}) => {
  trackEvent('Build Tracker', action, metadata.issueTitle || '');
  
  ReactGA.event('build_tracker_action', {
    action: action,
    ...metadata,
  });
};

// Track AI Interaction
export const trackAIInteraction = (action, context = '') => {
  trackEvent('AI Partner', action, context);
  
  ReactGA.event('ai_interaction', {
    action: action,
    context: context,
  });
};

// Track Connection Actions
export const trackConnectionEvent = (action, metadata = {}) => {
  trackEvent('Connections', action, metadata.connectionId || '');
  
  ReactGA.event('connection_action', {
    action: action,
    ...metadata,
  });
};

// Track Tool Usage (Time Blocks, Decisions, etc.)
export const trackToolUsage = (toolName, action) => {
  trackEvent('Tools', action, toolName);
  
  ReactGA.event('tool_usage', {
    tool: toolName,
    action: action,
  });
};

// Track Drop-offs (when users leave without completing an action)
export const trackDropOff = (funnel, step) => {
  trackEvent('Drop Off', funnel, step);
  
  ReactGA.event('drop_off', {
    funnel: funnel,
    step: step,
  });
};

// Set User Properties (for user segmentation)
export const setUserProperties = (properties) => {
  ReactGA.set(properties);
  
  if (process.env.NODE_ENV === 'production') {
    Sentry.setUser({
      id: properties.userId,
      email: properties.email,
      username: properties.username,
    });
  }
};

// Track Errors Manually
export const trackError = (error, errorInfo) => {
  console.error('Error tracked:', error, errorInfo);
  
  if (process.env.NODE_ENV === 'production') {
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo?.componentStack,
        },
      },
    });
  }
};

// Track Performance Metrics
export const trackPerformance = (metricName, value) => {
  trackEvent('Performance', metricName, '', value);
  
  if (process.env.NODE_ENV === 'production') {
    Sentry.addBreadcrumb({
      category: 'performance',
      message: `${metricName}: ${value}ms`,
      level: 'info',
    });
  }
};

export default {
  initSentry,
  initGA4,
  trackPageView,
  trackEvent,
  trackLogin,
  trackRegistration,
  trackFeatureUsage,
  trackBuildTrackerEvent,
  trackAIInteraction,
  trackConnectionEvent,
  trackToolUsage,
  trackDropOff,
  setUserProperties,
  trackError,
  trackPerformance,
};
